import type { CryptoType } from '@/hooks/useGetCrypto';
import useGetCrypto from '@/hooks/useGetCrypto';
import { Stack, useTheme } from '@mui/material';
import Paper from '@/components/Paper';
import Typography from '@/components/Typography';
import Chip from '@/components/Chip';
import map from 'lodash/map';
import Image from 'next/image';
import useLiveCrypto from '@/hooks/useLiveCrypto';
import useResponsive from '@/hooks/useResponsive';

const source = 'otc';

const Row = ({ data, pingPong }: { data: CryptoType; pingPong: boolean }) => {
  const theme = useTheme();

  const handleChipColor = (change: number) => {
    if (change < 0)
      return { color: 'error' as const, labelColor: theme.palette.error.A100 };
    if (change > 0)
      return {
        color: 'success' as const,
        labelColor: theme.palette.success.A100
      };
    return {
      color: 'secondary' as const
    };
  };

  const calculateChangePrice = (change?: number) => {
    if (change) return `${change}%`;
    return '0%';
  };

  const { message, isConnected } = useLiveCrypto({
    source,
    symbol: data.symbol,
    connect: true,
    pingPong: pingPong
  });

  return (
    // <Paper elevation={14} sx={{ borderRadius: 3 }}>
    <Stack
      flexDirection="row"
      alignItems="center"
      py={2}
      sx={{ borderBottom: '1px solid #EFF2F5' }}
    >
      <Stack
        flex={1}
        gap={3}
        flexDirection="row"
        justifyContent="start"
        alignItems="center"
      >
        <Image
          src={data.logoUrl || ''}
          width={30}
          height={30}
          alt={data.base}
        />
        <Stack gap={0.5} justifyContent="center">
          <Typography
            variant="body2"
            color="textColor.A80"
            weight="bold"
            noWrap
            maxWidth={85}
          >
            {data.baseFa}
          </Typography>
          <Typography variant="body3" color="textColor.A60">
            {data.base}
          </Typography>
        </Stack>
      </Stack>
      <Stack flex={1} gap={0.5} justifyContent="center" alignItems="end">
        <Stack gap={0.5} alignItems="center" flexDirection="row">
          <Typography variant="body2" color="textColor.A80" weight="bold">
            {isConnected && !!message?.midPrice
              ? message.midPrice
              : data[source]?.midPrice}
          </Typography>
          <Typography variant="body3" color="textColor.A60">
            تومان
          </Typography>
        </Stack>
        <Chip
          sx={{ direction: 'rtl' }}
          variant="filled"
          size="small"
          mode="light"
          label={calculateChangePrice(Number(data[source]?.priceChange || 0))}
          {...handleChipColor(Number(data[source]?.priceChange || 0))}
        />
      </Stack>
    </Stack>
    // </Paper>
  );
};

const OtherMarkets = ({ symbols }: { symbols: string[] }) => {
  const { matches } = useResponsive();
  const { result } = useGetCrypto(
    { get: { side: source, filter: symbols } },
    'getSidebarList',
    symbols
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        border: { lg: '3px solid #F8F8F8', xs: 'none' },
        borderBottom: { xs: '3px solid #F8F8F8' },
        width: matches({ xs: '100vw', lg: 392 }),
        px: 6,
        py: 4
      }}
    >
      <Stack flexDirection="row" alignItems="center">
        <Typography
          flex={1}
          weight="bold"
          textAlign="start"
          variant="body1"
          color="#142447"
        >
          قیمت سایر ارزهای دیجیتال
        </Typography>
      </Stack>
      {map(result || [], (row: CryptoType, index) => (
        <a
          key={row.id}
          href={`/price/${row.base.toLowerCase()}`}
          style={{ textDecoration: 'none' }}
        >
          <Row data={row} pingPong={index === 0} />
        </a>
      ))}
      <Stack pt={3}>
        <a href="/price" style={{ textDecoration: 'none' }}>
          <Typography
            flex={1}
            weight="bold"
            textAlign="center"
            color="primary.A100"
            sx={{
              fontSize: 14,
              lineHeight: '24px'
            }}
          >
            مشاهده بیشتر
          </Typography>
        </a>
      </Stack>
    </Paper>
  );
};

export default OtherMarkets;
